import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { ClassWebsiteView } from "@shared/models/analytics/class-website"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStack } from "@shared/models/card-stack"
import type { ClassWebsite } from "@shared/models/class-website"
import { partial } from "lodash-es"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultClassWebsiteActivity from "./default-class-website-activity"
import defaultClassWebsiteModels from "./default-class-website-models"

interface Payload {
  perspective: "day" | "week" | "lesson" | "unit"
  planbookId: string
  websiteId: string
  classWebsite: ClassWebsite
  lesson?: CardStack
}

export default function classWebsiteViewAction(payload: Payload): PreparedAction {
  return {
    name: "CLASS_WEBSITE_VIEW",
    payload: payload,
    patches: null,
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultClassWebsiteModels(payload, findFn, userId)
  let description: string
  if (payload.perspective === "lesson") {
    description = `User viewed the ${payload.lesson?.attributes.title} lesson`
  } else if (payload.perspective === "day") {
    description = `User viewed the class website on the Day view`
  } else if (payload.perspective === "unit") {
    description = `User viewed the class website on the Unit view`
  } else {
    description = `User viewed the class website on the Week view`
  }

  let newEvent: ClassWebsiteView = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "view",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "class_website",
    primary_object_id: payload.websiteId,
    primary_object_specifier: payload.perspective,
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],

    adverbial_verb: null,

    adverbial_object_type: null,
    adverbial_object_id: null,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],
  }

  return {
    title: "Viewed Class Website",
    titleForAnalytics: null,
    description: description,
    context: {
      websiteId: payload.websiteId,
      websiteURL: `www.commonplanner.com/sites/${payload.classWebsite.attributes.slug}`,
      perspective: payload.perspective,
    },
    activity: defaultClassWebsiteActivity(payload, models, "created"),
    newAnalyticsEvents: [newEvent],
  }
}
